import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Search, ShieldCheck, ShieldOff, Users as UsersIcon,
  Video, Mail, ChevronRight,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, isAdmin, activeOrg } from "../contexts/AuthContext";
import { trainingLabel } from "../lib/training";

const LEAGUE_LABEL = { NBA: "NBA", NCAA: "NCAA M", WNCAA: "NCAA W", WNBA: "WNBA", PRO: "PRO", HS: "HS" };

const ROLE_STYLE = {
  family:  { color: "#8ec5ff", label: "Family",  hint: "Parents · outputs, analytics, film, IQ, training, recruiting (read-only)." },
  player:  { color: "#4ade80", label: "Player",  hint: "The player's own login · outputs only, read-only." },
  trainer: { color: "#c084fc", label: "Trainer", hint: "Read-only + Vibes · can edit the Workout & Meeting Log." },
  agent:   { color: "#f0a35b", label: "Agent / Coach", hint: "Agent or team coach · read-only, includes Vibes." },
  lite:    { color: "#9ca3af", label: "Lite", hint: "Minimal dashboard · bio, review, archetype, role, film, IQ, routines (read-only)." },
};

export default function MemberDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const active = activeOrg(me);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [leagueFilter, setLeagueFilter] = useState("ALL");
  const [busyId, setBusyId] = useState(null);        // player_id being toggled
  const [roleBusy, setRoleBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.getMemberAccess(userId)
      .then(setData)
      .catch((err) => {
        toast.error("Couldn't load member", { description: err?.response?.data?.detail || err.message });
        setData(null);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [userId]);

  const filteredPlayers = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.players.filter(p => {
      if (leagueFilter !== "ALL" && p.league !== leagueFilter) return false;
      if (q && !(p.name || "").toLowerCase().includes(q) && !(p.team || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, query, leagueFilter]);

  const grantedCount = useMemo(() => (data?.players || []).filter(p => p.granted).length, [data]);

  // Group filtered players by training level so admins scan by tier (Maximize · Platinum · Workouts · Unassigned).
  const TIER_ORDER = ["Maximize", "The Program Platinum", "Workouts", "Unassigned"];
  const TIER_ACCENT = {
    "Maximize":              "var(--org-accent)",
    "The Program Platinum":  "#c084fc",
    "Workouts":              "#8ec5ff",
    "Unassigned":            "#575757",
  };
  const groupedPlayers = useMemo(() => {
    const buckets = { "Maximize": [], "The Program Platinum": [], "Workouts": [], "Unassigned": [] };
    for (const p of filteredPlayers) {
      const t = p.training_level;
      if (t === "Maximize" || t === "The Program Platinum" || t === "Workouts") buckets[t].push(p);
      else buckets["Unassigned"].push(p);
    }
    return TIER_ORDER.map(tier => ({ tier, players: buckets[tier], grantedCount: buckets[tier].filter(p => p.granted).length }))
                     .filter(g => g.players.length > 0);
  }, [filteredPlayers]);

  const toggle = async (p) => {
    if (!data) return;
    setBusyId(p.id);
    // Optimistic — flip locally, revert on failure.
    setData(d => ({
      ...d,
      players: d.players.map(x => x.id === p.id ? { ...x, granted: !x.granted } : x),
    }));
    try {
      if (p.granted) await api.removeMemberAccess(userId, p.id);
      else await api.addMemberAccess(userId, p.id);
    } catch (err) {
      // revert
      setData(d => ({
        ...d,
        players: d.players.map(x => x.id === p.id ? { ...x, granted: p.granted } : x),
      }));
      toast.error("Update failed", { description: err?.response?.data?.detail || err.message });
    } finally { setBusyId(null); }
  };

  const changeRole = async (newRole) => {
    if (!active) return;
    setRoleBusy(true);
    try {
      await api.updateOrgMemberRole(active.id, userId, newRole);
      setData(d => ({ ...d, member: { ...d.member, role: newRole } }));
      toast.success(`Role changed to ${newRole}`);
    } catch (err) {
      toast.error("Couldn't change role", { description: err?.response?.data?.detail || err.message });
    } finally { setRoleBusy(false); }
  };

  if (!isAdmin(me)) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] mb-3">403 · Admin Only</div>
          <div className="font-heading text-3xl text-white uppercase">This area is restricted</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 py-6 space-y-6" data-testid="member-detail">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/users")} className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.3em] text-[#a0a0a0] hover:text-white" data-testid="back-to-members">
            <ArrowLeft className="w-3 h-3" /> Members
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-12"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : !data ? (
          <div className="text-[#a0a0a0] text-sm">Member not found.</div>
        ) : (
          <>
            {/* Member header */}
            <section className="border border-white/10 bg-[#141414] p-5" data-testid="member-header">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Member</div>
              <div className="flex items-start justify-between gap-4 flex-wrap mt-1">
                <div className="min-w-0">
                  <div className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white leading-tight">
                    {data.member.name || data.member.email}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[12px] text-[#a0a0a0]">
                    <Mail className="w-3.5 h-3.5" />
                    <a href={`mailto:${data.member.email}`} className="hover:text-white break-all">{data.member.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#757575]">Role</span>
                  <select
                    value={data.member.role || "family"}
                    onChange={(e) => changeRole(e.target.value)}
                    disabled={roleBusy}
                    className="h-9 px-3 bg-[#101010] border border-white/10 text-xs font-bold uppercase tracking-widest focus:outline-none focus:border-[var(--org-accent)]"
                    style={{ color: ROLE_STYLE[data.member.role]?.color || "#c0c0c0" }}
                    data-testid="member-role-select"
                  >
                    <option value="family">Family</option>
                    <option value="player">Player</option>
                    <option value="trainer">Trainer</option>
                    <option value="agent">Agent / Coach</option>
                    <option value="lite">Lite</option>
                  </select>
                </div>
              </div>
              <div className="mt-3 text-[12px] text-[#a0a0a0] flex items-center gap-2">
                {data.member.role === "trainer" ? <Video className="w-3.5 h-3.5 text-[#c084fc]" /> : <UsersIcon className="w-3.5 h-3.5 text-[#8ec5ff]" />}
                {ROLE_STYLE[data.member.role]?.hint || "Read-only access to granted players."}
              </div>
              <div className="mt-4 flex items-center gap-4 text-[11px] uppercase tracking-[0.25em] text-[#757575]">
                <span><span className="text-white font-bold">{grantedCount}</span> of {data.players.length} players granted</span>
              </div>
            </section>

            {/* Player access checklist */}
            <section className="border border-white/10 bg-[#141414]" data-testid="member-players">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-wrap gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)]">Player Access</div>
                  <div className="font-heading text-xl uppercase tracking-tight text-white">Toggle Access Per Player</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-[#757575] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search players..."
                      className="h-9 pl-8 pr-3 bg-[#101010] border border-white/10 text-sm text-white w-56 focus:outline-none focus:border-[var(--org-accent)]"
                      data-testid="member-players-search"
                    />
                  </div>
                  <select
                    value={leagueFilter}
                    onChange={(e) => setLeagueFilter(e.target.value)}
                    className="h-9 px-3 bg-[#101010] border border-white/10 text-xs uppercase tracking-widest text-white focus:outline-none focus:border-[var(--org-accent)]"
                    data-testid="member-league-filter"
                  >
                    {["ALL", "NBA", "NCAA", "WNCAA", "WNBA", "PRO", "HS"].map(l =>
                      <option key={l} value={l}>{l === "ALL" ? "All Leagues" : LEAGUE_LABEL[l] || l}</option>
                    )}
                  </select>
                </div>
              </div>
              {groupedPlayers.length === 0 ? (
                <div className="px-5 py-8 text-center text-[12px] italic text-[#575757]">No players match.</div>
              ) : (
                <div>
                  {groupedPlayers.map(g => (
                    <div key={g.tier}>
                      <div
                        className="px-5 py-2 border-b border-white/5 flex items-center justify-between bg-[#141414]"
                        style={{ borderLeft: `3px solid ${TIER_ACCENT[g.tier]}` }}
                      >
                        <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: TIER_ACCENT[g.tier] }}>
                          {g.tier}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#757575]">
                          <span className="text-white font-bold">{g.grantedCount}</span> / {g.players.length}
                        </div>
                      </div>
                      <ul className="divide-y divide-white/5">
                        {g.players.map(p => (
                          <li key={p.id} className="flex items-center gap-3 px-5 py-3 hover:bg-white/[0.02]" data-testid={`member-player-row-${p.id}`}>
                            <button
                              type="button"
                              onClick={() => toggle(p)}
                              disabled={busyId === p.id}
                              className={`w-6 h-6 border-2 flex items-center justify-center shrink-0 ${p.granted ? "" : "hover:border-white/30"}`}
                              style={p.granted
                                ? { background: "var(--org-accent)", borderColor: "var(--org-accent)" }
                                : { background: "#0f0f0f", borderColor: "#333" }}
                              title={p.granted ? "Revoke access" : "Grant access"}
                              data-testid={`member-player-toggle-${p.id}`}
                            >
                              {busyId === p.id
                                ? <Loader2 className="w-3 h-3 animate-spin" style={{ color: p.granted ? "#121212" : "#a0a0a0" }} />
                                : p.granted && <ShieldCheck className="w-3.5 h-3.5 text-[#121212]" strokeWidth={3} />}
                            </button>
                            <Link
                              to={`/home/${p.id}`}
                              className="flex-1 min-w-0 flex items-center justify-between gap-3 group"
                              data-testid={`member-player-open-${p.id}`}
                            >
                              <div className="min-w-0">
                                <div className="text-sm text-white font-bold truncate">{p.name}</div>
                                <div className="text-[11px] text-[#757575] truncate">
                                  {LEAGUE_LABEL[p.league] || p.league}{p.team ? ` · ${p.team}` : ""}
                                  {p.training_level ? ` · ${trainingLabel(p.training_level, p.training_sub_tier, { compact: true })}` : ""}
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-[#575757] group-hover:text-[var(--org-accent)] shrink-0" />
                            </Link>
                            {p.granted ? (
                              <span className="text-[9px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 shrink-0" style={{ color: "#121212", background: "var(--org-accent)" }}>
                                Granted
                              </span>
                            ) : (
                              <span className="text-[9px] uppercase tracking-[0.3em] text-[#575757] shrink-0 flex items-center gap-1">
                                <ShieldOff className="w-3 h-3" /> No access
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
